import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { chaptersAPI, booksAPI, progressAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
    ChevronLeft, 
    ChevronRight, 
    Loader2,
    Settings,
    BookOpen,
    Home
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Slider } from '../components/ui/slider';

export default function ChapterReader() {
    const { chapterId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [chapter, setChapter] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fontSize, setFontSize] = useState(18);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        loadChapter();
    }, [chapterId]);

    const loadChapter = async () => {
        setLoading(true);
        try {
            const chapterRes = await chaptersAPI.getOne(chapterId);
            setChapter(chapterRes.data);
            
            // Load all chapters for navigation
            const chaptersRes = await booksAPI.getChapters(chapterRes.data.book_id);
            setChapters(chaptersRes.data);
            
            const idx = chaptersRes.data.findIndex(c => c.id === chapterId);
            setCurrentIndex(idx);
            
            // Update reading progress
            if (user) {
                try {
                    await progressAPI.update({
                        chapter_id: chapterId,
                        progress: 100
                    });
                } catch (err) {
                    console.log('Could not update progress');
                }
            }
        } catch (err) {
            console.error('Failed to load chapter:', err);
        } finally {
            setLoading(false);
        }
    };

    const goToChapter = (index) => {
        if (index >= 0 && index < chapters.length) {
            navigate(`/book/chapter/${chapters[index].id}`);
        }
    };

    const fontSizeClass = {
        14: 'reader-sm',
        16: 'reader-base',
        18: 'reader-lg',
        20: 'reader-xl',
        22: 'reader-2xl'
    }[fontSize] || 'reader-lg';

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!chapter) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h1 className="font-heading text-2xl font-bold mb-4">Chapter Not Found</h1>
                <Button onClick={() => navigate('/book')} className="rounded-full">
                    Back to Book
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Reader Header */}
            <div className="sticky top-16 z-40 bg-background/95 backdrop-blur border-b">
                <div className="container mx-auto px-4 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate('/book')}
                            data-testid="back-to-book"
                        >
                            <Home className="h-5 w-5" />
                        </Button>
                        <span className="text-sm text-muted-foreground hidden sm:inline">
                            Chapter {currentIndex + 1} of {chapters.length}
                        </span>
                    </div>
                    
                    <h1 className="font-medium text-sm truncate max-w-[200px] sm:max-w-xs">
                        {chapter.title}
                    </h1>
                    
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="reader-settings">
                                <Settings className="h-5 w-5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                            <DropdownMenuLabel>Reading Settings</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <div className="p-3">
                                <p className="text-sm mb-3">Font Size: {fontSize}px</p>
                                <Slider
                                    value={[fontSize]}
                                    onValueChange={([v]) => setFontSize(v)}
                                    min={14}
                                    max={22}
                                    step={2}
                                    data-testid="font-size-slider"
                                />
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Reader Content */}
            <motion.article
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="max-w-3xl mx-auto px-4 py-8 md:py-12"
            >
                <header className="mb-8 text-center">
                    <p className="text-primary font-medium mb-2">Chapter {currentIndex + 1}</p>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold">{chapter.title}</h1>
                </header>

                <div 
                    className={`font-reader ${fontSizeClass} text-foreground/90`}
                    style={{ fontSize: `${fontSize}px` }}
                    data-testid="chapter-content"
                >
                    {chapter.content.split('\n').map((paragraph, i) => (
                        paragraph.trim() && (
                            <p key={i} className="mb-6 leading-relaxed">
                                {paragraph}
                            </p>
                        )
                    ))}
                </div>

                {/* Navigation */}
                <Card className="mt-12 p-4">
                    <div className="flex items-center justify-between">
                        <Button
                            variant="outline"
                            className="rounded-full gap-2"
                            onClick={() => goToChapter(currentIndex - 1)}
                            disabled={currentIndex === 0}
                            data-testid="prev-chapter"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Previous</span>
                        </Button>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <BookOpen className="h-4 w-4" />
                            {currentIndex + 1} / {chapters.length}
                        </div>
                        
                        <Button
                            className="rounded-full gap-2"
                            onClick={() => goToChapter(currentIndex + 1)}
                            disabled={currentIndex === chapters.length - 1}
                            data-testid="next-chapter"
                        >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </Card>
            </motion.article>
        </div>
    );
}
